import { useMemo } from 'react'

import { useLoad } from '@/hooks/shipments'

import type {
  CalendarBucketStatus,
  CalendarShipmentItem,
  CalendarShipmentSummary,
  DayBucket,
  DayPanelShipment,
  WeekRange,
} from './types'
import {
  type CalendarCell,
  getShipmentEntries,
  mapStatusToDayPanelTab,
  toCalendarDateKey,
} from './utils'

export function useShipmentsForDay(
  date: Date | null,
  shipments: CalendarShipmentItem[],
): DayPanelShipment[] {
  return useMemo(() => {
    if (!date) {
      return []
    }

    const dateKey = toCalendarDateKey(date)
    return shipments.flatMap(s =>
      s.entries
        .filter(entry => entry.dateKey === dateKey)
        .map(entry => ({
          tab: mapStatusToDayPanelTab(entry.status),
          bolNumber: s.bolNumber,
          loadId: s.loadId,
        })),
    )
  }, [date, shipments])
}

export const usePrefetchAdjacentLoads = (
  shipments: DayPanelShipment[],
  currentIndex: number,
) => {
  const prevLoadId =
    currentIndex > 0 ? shipments[currentIndex - 1]?.loadId : undefined
  const currentLoadId = shipments[currentIndex]?.loadId
  const nextLoadId =
    currentIndex < shipments.length - 1
      ? shipments[currentIndex + 1]?.loadId
      : undefined

  useLoad(prevLoadId)
  useLoad(currentLoadId)
  useLoad(nextLoadId)
}

export function useShipmentsWithEntries(
  summaries: CalendarShipmentSummary[],
): CalendarShipmentItem[] {
  return useMemo(() => {
    return summaries.map(shipment => ({
      ...shipment,
      entries: getShipmentEntries(shipment),
    }))
  }, [summaries])
}

export function useBucketsByDate(
  shipments: CalendarShipmentItem[],
): Map<string, DayBucket[]> {
  return useMemo(() => {
    const talliesByDate = new Map<
      string,
      Record<CalendarBucketStatus, number>
    >()

    for (const shipment of shipments) {
      for (const entry of shipment.entries) {
        let tallies = talliesByDate.get(entry.dateKey)
        if (!tallies) {
          tallies = {
            scheduled: 0,
            'in-transit': 0,
            'out-for-delivery': 0,
            delivered: 0,
          }
          talliesByDate.set(entry.dateKey, tallies)
        }
        tallies[entry.status] += 1
      }
    }

    const bucketsMap = new Map<string, DayBucket[]>(
      Array.from(talliesByDate.entries()).map(([dateKey, tallies]) => {
        const buckets: DayBucket[] = [
          { status: 'scheduled' as const, count: tallies.scheduled },
          { status: 'in-transit' as const, count: tallies['in-transit'] },
          {
            status: 'out-for-delivery' as const,
            count: tallies['out-for-delivery'],
          },
          { status: 'delivered' as const, count: tallies.delivered },
        ].filter(b => b.count > 0)
        return [dateKey, buckets]
      }),
    )

    return bucketsMap
  }, [shipments])
}

type CalendarData = {
  cells: CalendarCell[]
  rows: number
  startDate: Date
  endDate: Date
  monthKey: string
  weeks: WeekRange[]
}

function buildCalendarGrid(anyDayInMonth: Date): CalendarData {
  const year = anyDayInMonth.getFullYear()
  const month = anyDayInMonth.getMonth()

  const firstOfMonth = new Date(year, month, 1)
  const leadDays = firstOfMonth.getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const totalCells = leadDays + daysInMonth
  const rows = Math.ceil(totalCells / 7)
  const cellCount = rows * 7

  const gridStart = new Date(year, month, 1 - leadDays)
  const gridEnd = new Date(gridStart)
  gridEnd.setDate(gridStart.getDate() + cellCount - 1)

  const cells: CalendarCell[] = Array.from({ length: cellCount }, (_, i) => {
    const date = new Date(gridStart)
    date.setDate(gridStart.getDate() + i)
    return {
      date,
      dayOfMonth: date.getDate(),
      isCurrentMonth: date.getMonth() === month,
    }
  })

  const weeks: WeekRange[] = []
  for (let i = 0; i < cells.length; i += 7) {
    const weekCells = cells.slice(i, i + 7)
    const start = weekCells[0].date
    const end = weekCells[weekCells.length - 1].date
    weeks.push({
      start,
      end,
      key: `${toCalendarDateKey(start)}_${toCalendarDateKey(end)}`,
    })
  }

  const monthKey = `${year}-${String(month).padStart(2, '0')}`

  return {
    cells,
    rows,
    startDate: gridStart,
    endDate: gridEnd,
    monthKey,
    weeks,
  }
}

export function useCalendar(anyDayInMonth: Date): CalendarData {
  const year = anyDayInMonth.getFullYear()
  const month = anyDayInMonth.getMonth()

  return useMemo(() => {
    return buildCalendarGrid(new Date(year, month, 1))
  }, [year, month])
}
